/* eslint-disable @typescript-eslint/no-explicit-any */
import aes from 'crypto-js/aes';
import enc from 'crypto-js/enc-utf8';
import { IAnalyticsEvent } from '@verv-inc/js-toolkit/core-analytics';
import { IErrorLoggerWithUserSupport } from '@verv-inc/js-toolkit/core-logs/lib/ErrorLoggerWithUserSupport';

import { getParameterFromURL } from '../scripts';
import { IEveryDoggyAPIServices } from '../services/EveryDoggyAPIServices';
import OneLinkParametersHelper from '../services/OneLinkParametersComposer';
import { StripeCreateSubscriptionRequest } from '../services/EveryDoggyAPITypes';
import { analyticsEventsTracker, EVENT_TRACKERS } from '../analytics';
import { extractEmailFromUrl } from '../scripts/extractEmailFromUrl';
import getAmplitudeDeviceId from '../scripts/amplitudeDeviceId';

/**
 * `ev` carries base64 AES ciphertext, not form-encoded text — unlike
 * `getParameterFromURL`, this must not turn literal `+` into a space, or the
 * ciphertext (and its embedded salt) gets corrupted and decryption fails with
 * "Malformed UTF-8 data".
 */
function getEncryptedEmailParam(url: string): string {
  const match = /[?&]ev=([^&#]*)/.exec(url);
  return match ? decodeURIComponent(match[1]) : '';
}

export type PaymentActionResult = {
  url: string;
};

export interface IStripeViewModel {
  getStripeKey: () => Promise<string>;
  initialEmail: () => string | null;
  processPaymentAction: (
    paymentMethodId: string,
    email: string,
    stripeAmount: string,
    stripelName: string,
    answers?: any,
    stripe?: any
  ) => Promise<PaymentActionResult>;
  processPaymentCancel: () => Promise<void>;
  processPaymentError: (error: any) => Promise<void>;
}

export type StripeSuccessEventParams = {
  productName: string;
  amount: string;
  email: string;
};

/**
 * Shared Stripe checkout view-model used by both live payment modals
 * (paymentModalV5 and chatUpsaleV2). The only flow-specific piece is the
 * GTM success event, injected via `buildPaymentSuccessEvent`.
 */
export function composeStripeCheckoutViewModel(
  sourceURL: string,
  stripePlanID: string,
  apiServices: IEveryDoggyAPIServices,
  errorLogger: IErrorLoggerWithUserSupport,
  internalId: string,
  buildPaymentSuccessEvent: (params: StripeSuccessEventParams) => IAnalyticsEvent
): IStripeViewModel {
  const oneLinkParametersHelper = OneLinkParametersHelper;

  // returns Stripe key
  const getStripeKey = async () => {
    let data = await apiServices.getStripeConfig();
    if (!data) data = await apiServices.getStripeConfig();
    if (!data?.publishableKey) {
      throw new Error('Stripe config unavailable');
    }
    return data.publishableKey;
  };

  const initialEmail = () => {
    let emailInputValue = getParameterFromURL('emailValue', sourceURL);
    const emailInputValueFromUrl = getEncryptedEmailParam(sourceURL);

    if (emailInputValueFromUrl) {
      try {
        const emailInputValueRaw = aes.decrypt(emailInputValueFromUrl, '9213812384');
        emailInputValue = emailInputValueRaw.toString(enc);
      } catch (error) {
        errorLogger.logError({
          name: 'Stripe error',
          message: `failed to decrypt "ev" param: ${error}`,
        });
      }
    }

    return emailInputValue;
  };

  const processPaymentAction = async (
    paymentMethodId: string,
    email: string,
    stripeAmount: string,
    stripelName: string,
    answers: any,
    stripe: any
  ) => {
    if (!paymentMethodId) {
      errorLogger.logError({
        name: 'Stripe error',
        message: 'no paymentMethodId',
      });
    }
    if (!email) {
      errorLogger.logError({
        name: 'Stripe error',
        message: 'no buyer email',
      });
    }

    const dataToSend: StripeCreateSubscriptionRequest = {
      amplitude_device_id: getAmplitudeDeviceId(),
      payment_method_id: paymentMethodId,
      email: email,
      stripe_plan_id: stripePlanID,
      internal_id: internalId,
      fbclickid: oneLinkParametersHelper.getFBclickId(sourceURL),
      fbp: oneLinkParametersHelper.getFBp(sourceURL),
      source_url: sourceURL,
      onboarding_email: extractEmailFromUrl() || email,
      ...(answers !== null && answers !== undefined && { onboarding_answers: answers }),
    };

    return apiServices
      .createStripeSubscription(dataToSend, 'stripe', stripelName, stripeAmount, email, stripe)
      .then(_ => apiServices.checkoutStripeSubscription(email))
      .then(result => {
        const event = buildPaymentSuccessEvent({
          productName: `${stripelName}`,
          amount: `${stripeAmount}`,
          email,
        });
        analyticsEventsTracker.trackWithTarget(event, EVENT_TRACKERS.GTM);
        //FIXME: validate URL
        const url = result.redirect_url;
        return { url };
      });
  };

  const processPaymentCancel = async () => {
    errorLogger.logMessage('stripe payment cancelled');
  };

  const processPaymentError = async (error: any) => {
    errorLogger.logError({
      name: 'Stripe error',
      message: JSON.stringify(error),
    });
  };

  return {
    getStripeKey,
    initialEmail,
    processPaymentAction,
    processPaymentCancel,
    processPaymentError,
  };
}
